/**
 * Graph cell style constants for the visual projection layer.
 * Per SDD-06 §4 — Material nodes, complex ops, operation edges,
 * lineage edges, flow edges, and group (swimlane) cells.
 */
import type { CellStyle } from '@maxgraph/core';

// ─── Material Nodes (§4.1) ──────────────────────────────────────────────────

export const MATERIAL_STYLE: CellStyle = {
  shape: 'ellipse',
  fillColor: '#EFF6FF',
  strokeColor: '#1E40AF',
  fontColor: '#1E3A8A',
  fontSize: 12,
  fontStyle: 1, // bold
  verticalAlign: 'middle',
  align: 'center',
  perimeter: 'ellipsePerimeter',
  whiteSpace: 'wrap',
};

export const MATERIAL_REMAINDER_STYLE: CellStyle = {
  shape: 'ellipse',
  fillColor: '#FEF3C7',
  strokeColor: '#D97706',
  fontColor: '#92400E',
  fontSize: 12,
  fontStyle: 1, // bold
  verticalAlign: 'middle',
  align: 'center',
  perimeter: 'ellipsePerimeter',
  whiteSpace: 'wrap',
};

export const MATERIAL_DIMENSIONS = { width: 100, height: 100 } as const;

// ─── Complex Operation Nodes (§4.2) ─────────────────────────────────────────

export const COMPLEX_OP_STYLE: CellStyle = {
  shape: 'rectangle',
  rounded: true,
  fillColor: '#dbeafe',
  strokeColor: '#2563eb',
  fontColor: '#1e3a5f',
  fontSize: 11,
  fontStyle: 1,
  perimeter: 'rectanglePerimeter',
};

export const COMPLEX_OP_DIMENSIONS = { width: 140, height: 50 } as const;

// ─── Operation Edges — collapsed simple ops (§4.3) ──────────────────────────

export const OPERATION_EDGE_STYLE: CellStyle = {
  strokeColor: '#1e40af',
  strokeWidth: 2,
  fontSize: 10,
  fontColor: '#4b5563',
  // Smooth curved routing: the HierarchicalLayout inserts POLYLINE control
  // points (disableEdgeStyle=false), and these keys render them as a rounded
  // curve that bends around ranks instead of crossing straight.
  rounded: true,
  curved: true,
  // Label appears beside (to the left of) the edge line:
  // 'right' alignment means the text's right edge is anchored at the edge
  // midpoint, so the text extends leftward — placing it beside the arrow
  // rather than straddling the line.
  align: 'right',
  verticalAlign: 'bottom',
  labelBackgroundColor: 'none',
  endArrow: 'classic',
  endSize: 8,
};

/** Remainder edges: same base as operation, slightly thinner. */
export const REMAINDER_EDGE_STYLE: CellStyle = {
  ...OPERATION_EDGE_STYLE,
  strokeWidth: 1.5,
};

// ─── Lineage Edges — dashed, hidden by default (§4.4) ──────────────────────

export const LINEAGE_EDGE_STYLE: CellStyle = {
  strokeColor: '#9ca3af',
  strokeWidth: 1,
  dashed: true,
  dashPattern: '6 3',
  endArrow: 'classic',
  endSize: 6,
};

// ─── Flow Edges — complex op ↔ materials (§4.5) ────────────────────────────

export const FLOW_EDGE_STYLE: CellStyle = {
  strokeColor: '#374151',
  strokeWidth: 1.5,
  rounded: true,
  curved: true,
  endArrow: 'classic',
  endSize: 8,
};

// ─── Link Edges — connecting disconnected branches (§4.5a) ─────────────────

export const LINK_EDGE_STYLE: CellStyle = {
  strokeColor: '#dc2626', // Red for visibility
  strokeWidth: 2,
  dashed: true,
  dashPattern: '6 3',
  rounded: true,
  curved: true,
  endArrow: 'classic',
  endSize: 8,
};

// ─── Group / Swimlane Cells (§4.6) ─────────────────────────────────────────

export const GROUP_STYLE: CellStyle = {
  shape: 'swimlane',
  startSize: 30,
  fillColor: '#f3f4f6',
  strokeColor: '#9ca3af',
  fontColor: '#374151',
  fontSize: 12,
  fontStyle: 1,
  foldable: true,
  rounded: true,
  // Push label text right so it doesn't overlap the 16px fold icon
  // (icon sits at ~x+4, so 4px padding + 16px icon + 4px gap = 24px)
  spacingLeft: 24,
};
